import { trackMealPlanWidgetClick, trackMealPlanWidgetLoad } from 'analytics/trackers/workout';
import { useCanAccessMealPlan } from 'navigation/useCanAccessMealPlan';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { PRIVATE } from 'router/routes';

import { selectIsNavReady } from 'store/features/selectors';

type MealPlanWidgetState = 'loading' | 'hidden' | 'shown';

const QUOTE_STORAGE_KEY = 'mb-meal-plan-quote';
const IMAGE_STORAGE_KEY = 'mb-meal-plan-image';

// Vite resolves each .webp under the meals folder to its built URL.
const mealImages = Object.values(
    import.meta.glob('../../../assets/images/meals/*.webp', { eager: true, import: 'default' })
) as string[];

const SUBTITLE_COUNT = 30;

const subtitleKeys = Array.from({ length: SUBTITLE_COUNT }, (_, index) => `nutritionWidget.subtitle${index + 1}`);

const getRandomQuote = (quotes: string[]): string => quotes[Math.floor(Math.random() * quotes.length)] ?? '';

const getRandomImage = (images: string[]): string => images[Math.floor(Math.random() * images.length)] ?? '';

const getPlanState = ({ isLoading, canAccess }: { isLoading: boolean; canAccess: boolean }): MealPlanWidgetState => {
    if (isLoading) {
        return 'loading';
    }

    return canAccess ? 'shown' : 'hidden';
};

const getImageVariant = (image: string): number => {
    const imageName = image.split('_').pop()?.split('.')[0] ?? '';

    return parseInt(imageName, 10) || 0;
};

const getTitleVariant = (title: string): number => {
    const titleName = title.split('subtitle').pop()?.split('.')[0] ?? '';

    return parseInt(titleName, 10) || 0;
};

export const useMealPlanWidget = ({ selectedDate }: { selectedDate: Date }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isNavReady = useSelector(selectIsNavReady);
    const canAccess = useCanAccessMealPlan();
    const state = getPlanState({ isLoading: !isNavReady, canAccess });

    const [quoteKey] = useState(() => localStorage.getItem(QUOTE_STORAGE_KEY) ?? getRandomQuote(subtitleKeys));
    const [image] = useState(() => localStorage.getItem(IMAGE_STORAGE_KEY) ?? getRandomImage(mealImages));

    useEffect(() => {
        const pickNext = () => {
            localStorage.setItem(QUOTE_STORAGE_KEY, getRandomQuote(subtitleKeys));
            localStorage.setItem(IMAGE_STORAGE_KEY, getRandomImage(mealImages));
        };

        window.addEventListener('beforeunload', pickNext);

        return () => {
            window.removeEventListener('beforeunload', pickNext);
        };
    }, []);

    const copy = {
        sectionHeader: t('nutritionWidget.sectionHeader'),
        title: t('nutritionWidget.title'),
        tag: t('nutritionWidget.tag'),
        subTitle: t(quoteKey),
    };
    const handleRedirect = () => {
        trackMealPlanWidgetClick({ imageVariant: getImageVariant(image), titleVariant: getTitleVariant(quoteKey) });
        navigate(PRIVATE.MEAL_PLAN.path);
    };

    const isToday = (date: Date): boolean => {
        const today = new Date();

        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    };

    const isWidgetShown = state === 'shown' && isToday(selectedDate);

    useEffect(() => {
        if (isWidgetShown) {
            trackMealPlanWidgetLoad();
        }
    }, [isWidgetShown]);

    return { widgetProps: { copy, image, onClick: handleRedirect }, state };
};
